/**
 * The Charter Board (M5 plan C17, spec §15.2, §18 flows 4 and 7): the header (claims per faction as a stacked bar
 * with marks, active wars, the gate status, a pending vote), the filter and sort buttons, one row per system and
 * the Votes panel. A row's name button focuses the galaxy map on it; its expand button shows the Ops, Sec and Inf
 * bars per faction. Plain buttons in DOM order, so the keyboard, the pad (`navigateButtons`) and M2's station
 * screen focus model reach every control. Rendered from the pure `boardModel`. A corporation-held row names the
 * corporation with its security band and tariff ("not set" when automatic; D-424 D5, spec §27.7).
 */
import { useState, type ReactNode } from 'react'
import type { RegionId } from '../../galaxy/types'
import { FACTION_IDS, type FactionId } from '../../strategic/types'
import {
  BOARD_SORTS,
  boardHeader,
  boardRows,
  DEFAULT_FILTER,
  factionLook,
  filterRows,
  REGION_LABELS,
  REGION_ORDER,
  SORT_LABELS,
  sortRows,
  stepOption,
  tollLabel,
  votesModel,
  type BoardConfig,
  type BoardFilter,
  type BoardInput,
  type BoardRow,
  type BoardSort,
  type FactionLook,
} from './boardModel'
import { registryNote } from './registryNote'
import { VotesPanel } from './VotesPanel'
import '../strategic/strategic.css'
import './charter.css'

/** Factions that can hold claims (the faction filter's options). */
const CLAIM_FACTIONS: readonly (FactionId | 'all')[] = ['all', ...FACTION_IDS.filter((f) => f !== 'raider' && f !== 'guardian')]
const REGIONS: readonly (RegionId | 'all')[] = ['all', ...REGION_ORDER]

export interface CharterBoardProps {
  input: BoardInput
  config: BoardConfig
  relayEnds?: ReadonlyMap<string, readonly [string, string]>
  initialFilter?: BoardFilter
  initialSort?: BoardSort
  /** A row was chosen: focus the galaxy map on its system. */
  onSelect?: (systemId: string) => void
  /** Shown as a close button when set (the overlay; a station tab has none). */
  onClose?: () => void
  /** True while the claim details are being read. */
  loading?: boolean
}

function Chip(p: { look: FactionLook | null }): ReactNode {
  if (p.look === null) return <span className="cb-chip cb-chip--none">○ none</span>
  return (
    <span className="cb-chip" style={{ borderColor: p.look.color, background: `${p.look.color}33` }} title={p.look.name}>
      {p.look.mark}
    </span>
  )
}

function Spark(p: { row: BoardRow; config: BoardConfig }): ReactNode {
  const { width, height } = p.config.sparkline
  if (p.row.sparks.length === 0) return <span className="cb-dim">—</span>
  return (
    <svg
      className="cb-spark"
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={p.row.sparks.map((s) => `${s.look.name} ${s.last === null ? '—' : Math.round(s.last)}`).join(', ')}
    >
      {p.row.sparks.map((s, i) => (
        <polyline key={s.faction} points={s.points} fill="none" stroke={s.look.color} strokeWidth={1.5} strokeDasharray={i === 0 ? undefined : '3 2'} />
      ))}
    </svg>
  )
}

function Bars(p: { row: BoardRow }): ReactNode {
  if (p.row.components.length === 0) return <p className="sui-empty">No faction scores here yet.</p>
  return (
    <table className="cb-bars" data-testid="charter-bars">
      <tbody>
        {p.row.components.map((c) => (
          <tr key={c.faction}>
            <td>
              <Chip look={c.look} /> {c.look.name}
            </td>
            {(['ops', 'sec', 'inf'] as const).map((k) => (
              <td key={k} className="cb-bar">
                <span className="cb-bar__label">
                  {k === 'ops' ? 'Ops' : k === 'sec' ? 'Sec' : 'Inf'} {Math.round(c[k])}
                </span>
                <span className="cb-bar__track">
                  <span
                    className="cb-bar__fill"
                    style={{
                      width: `${Math.max(0, Math.min(100, c[k]))}%`,
                      background: c.look.color,
                    }}
                  />
                </span>
              </td>
            ))}
            <td>S {Math.round(c.s)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function fmtS(s: number | null): string {
  return s === null ? '—' : String(Math.round(s))
}

export function CharterBoard(p: CharterBoardProps): ReactNode {
  const [filter, setFilter] = useState<BoardFilter>(p.initialFilter ?? DEFAULT_FILTER)
  const [sort, setSort] = useState<BoardSort>(p.initialSort ?? 'name')
  const [expanded, setExpanded] = useState<string | null>(null)
  const header = boardHeader(p.input)
  const all = boardRows(p.input, {
    sparkline: p.config.sparkline,
    relayEnds: p.relayEnds,
  })
  const rows = sortRows(filterRows(all, filter), sort)
  const votes = votesModel(p.input.votes, p.input.day, p.input.corp ?? null)
  const note = registryNote()
  return (
    <div className="sui cb-board" data-testid="charter-board">
      <header className="sui-head">
        <h2 className="sui-title">Charter Board</h2>
        {p.onClose !== undefined ? (
          <button type="button" className="sui-btn sui-close" data-testid="charter-close" onClick={p.onClose}>
            Close
          </button>
        ) : null}
        <div className="cb-header" data-testid="charter-header">
          <div
            className="cb-claimbar"
            role="img"
            aria-label={header.claims.map((c) => `${c.look.name} ${c.count}`).join(', ') + `, unclaimed ${header.unclaimed}`}
          >
            {header.claims.map((c) => (
              <span
                key={c.faction}
                className="cb-claimbar__seg"
                style={{
                  width: `${c.fraction * 100}%`,
                  background: c.look.color,
                }}
                title={`${c.look.name}: ${c.count}`}
              >
                {c.look.mark} {c.count}
              </span>
            ))}
            {header.unclaimed > 0 ? (
              <span
                className="cb-claimbar__seg cb-claimbar__seg--none"
                style={{
                  width: `${header.total > 0 ? (header.unclaimed / header.total) * 100 : 0}%`,
                }}
              >
                ○ {header.unclaimed}
              </span>
            ) : null}
          </div>
          <p className="sui-sub">
            {header.wars.length > 0 ? header.wars.map((w) => w.label).join(' · ') : 'No wars'} ·{' '}
            <span data-testid="charter-gate">{header.isolation ? `The Isolated Era · ${header.gate}` : header.gate}</span>
            {header.pendingVote !== null ? <> · {header.pendingVote}</> : null}
          </p>
          {note !== null ? (
            <p className="sui-sub cb-registry" data-testid="charter-registry-note">
              {note.text}
            </p>
          ) : null}
        </div>
      </header>
      <div className="sui-filters" aria-label="Board filters">
        <button
          type="button"
          className="sui-btn sui-filter"
          data-testid="charter-filter-region"
          onClick={() =>
            setFilter((f) => ({
              ...f,
              region: stepOption(REGIONS, f.region, 1),
            }))
          }
        >
          Region: {filter.region === 'all' ? 'All' : REGION_LABELS[filter.region]}
        </button>
        <button
          type="button"
          className="sui-btn sui-filter"
          data-testid="charter-filter-faction"
          onClick={() =>
            setFilter((f) => ({
              ...f,
              faction: stepOption(CLAIM_FACTIONS, f.faction, 1),
            }))
          }
        >
          Faction: {filter.faction === 'all' ? 'All' : factionLook(filter.faction).name}
        </button>
        <button
          type="button"
          className="sui-btn sui-filter"
          data-testid="charter-filter-contested"
          data-active={filter.contested ? '' : undefined}
          aria-pressed={filter.contested}
          onClick={() => setFilter((f) => ({ ...f, contested: !f.contested }))}
        >
          {filter.contested ? '▸ ' : ''}Contested
        </button>
        <button
          type="button"
          className="sui-btn sui-filter"
          data-testid="charter-filter-war"
          data-active={filter.atWar ? '' : undefined}
          aria-pressed={filter.atWar}
          onClick={() => setFilter((f) => ({ ...f, atWar: !f.atWar }))}
        >
          {filter.atWar ? '▸ ' : ''}At war
        </button>
        <button type="button" className="sui-btn sui-filter" data-testid="charter-sort" onClick={() => setSort((s) => stepOption(BOARD_SORTS, s, 1))}>
          Sort: {SORT_LABELS[sort]}
        </button>
      </div>
      {p.loading === true ? <p className="sui-empty">Reading the Charter registry…</p> : null}
      {rows.length === 0 ? <p className="sui-empty">No systems match.</p> : null}
      <table className="cb-table">
        <thead>
          <tr>
            <th>System</th>
            <th>Region</th>
            <th>Holder</th>
            <th>S</th>
            <th>Challenger</th>
            <th>Lead</th>
            <th>10 days</th>
            <th>States</th>
            <th>Tariff</th>
            <th>Toll</th>
            <th>Flip</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <BoardTableRow
              key={r.id}
              row={r}
              config={p.config}
              open={expanded === r.id}
              onSelect={p.onSelect}
              onToggle={() => setExpanded((e) => (e === r.id ? null : r.id))}
            />
          ))}
        </tbody>
      </table>
      <VotesPanel votes={votes} />
    </div>
  )
}

function BoardTableRow(p: { row: BoardRow; config: BoardConfig; open: boolean; onSelect?: (id: string) => void; onToggle: () => void }): ReactNode {
  const r = p.row
  return (
    <>
      <tr className="cb-row" data-contested={r.contested ? '' : undefined} data-war={r.atWar ? '' : undefined} data-testid="charter-row-line">
        <td>
          <button type="button" className="sui-btn cb-name" data-testid="charter-row" data-system={r.id} onClick={() => p.onSelect?.(r.id)}>
            {r.name}
            {r.contested ? <span className="cb-flag"> ▨ contested</span> : null}
            {r.atWar ? <span className="cb-flag cb-flag--war"> ⚔ war</span> : null}
          </button>
        </td>
        <td>{r.regionLabel}</td>
        <td>
          <Chip look={r.holderLook} />
          {r.corp !== null ? (
            <span className="cb-corp" data-testid="charter-row-corp">
              {' '}
              {r.corp.name}
              {r.corp.band !== null ? ` · security ${r.corp.band}` : ''}
            </span>
          ) : null}
        </td>
        <td>{fmtS(r.holderS)}</td>
        <td>
          {r.challengerLook !== null ? (
            <>
              <Chip look={r.challengerLook} /> {fmtS(r.challengerS)}
            </>
          ) : (
            '—'
          )}
        </td>
        <td>{r.lead === null ? '—' : Math.round(r.lead)}</td>
        <td>
          <Spark row={r} config={p.config} />
        </td>
        <td>
          {r.states.length === 0
            ? '—'
            : r.states.map((s) => (
                <span key={s.kind} className="cb-state" data-stage={s.stage} title={`${s.label} (${s.stage})`}>
                  {s.glyph} {s.label}
                </span>
              ))}
        </td>
        <td>{r.tariffText}</td>
        <td>{tollLabel(r.toll)}</td>
        <td>{r.flip === null ? '—' : `${r.flip.look.mark} in ${r.flip.daysLeft} d`}</td>
        <td>
          <button type="button" className="sui-btn cb-expand" data-testid="charter-expand" aria-expanded={p.open} onClick={p.onToggle}>
            {p.open ? '▾' : '▸'} Detail
          </button>
        </td>
      </tr>
      {p.open ? (
        <tr className="cb-detail">
          <td colSpan={12}>
            <Bars row={r} />
          </td>
        </tr>
      ) : null}
    </>
  )
}
